import parsePhoneNumberFromString from 'libphonenumber-js';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import * as Yup from 'yup';
import { TFileWithPreview } from './components/image-uploader/image-uploader';

export interface CreateWorkspaceForm {
  name: string;
  specialization: { value: string; label: string } | null;

  // type of institution
  institutionVariant: 'search' | 'custom';
  // Institution from search
  institution: { value: string; label: string } | null;
  // Custom address
  address: string;
  city: string;
  postCode: string;
  country: string;

  addressInfo: string | null;
  info: string;
  contactPhone: string;
  contactEmail: string;
  staff: [];
  medicalInsurances: { value: string; label: string }[];
  workspaceTags: string[];
  image: TFileWithPreview | null;
  workspaceId?: string;
}

export const initialValues: CreateWorkspaceForm = {
  name: '',
  specialization: null,
  institution: null,
  address: '',
  city: '',
  postCode: '',
  country: '',
  addressInfo: '',
  info: '',
  contactPhone: '',
  contactEmail: '',
  staff: [],
  medicalInsurances: [],
  workspaceTags: [],
  institutionVariant: 'search',
  image: null,
};

export const validationSchema = Yup.object().shape({
  name: Yup.string().required('validation.required'),
  specialization: Yup.mixed().required('validation.required'),
  institutionVariant: Yup.mixed().required('validation.required'),

  institution: Yup.mixed().when('institutionVariant', {
    is: (v: string) => v === 'search',
    then: (schema) => schema.required('validation.required'),
    otherwise: (schema) => schema.nullable(),
  }),
  address: Yup.string().when('institutionVariant', {
    is: (v: string) => v === 'custom',
    then: (schema) =>
      schema
        .test('required', 'validation.required_field', (address, { parent }) => {
          const city = parent.city;
          const country_code = parent.country;
          const post_code = parent.postCode;

          if (city || country_code || post_code) {
            return Boolean(address);
          }

          return true;
        })
        .test('validate-street-number', 'validation.address_format', (address) => {
          if (address !== null && address !== undefined) {
            return /\d/.test(address) && /[a-zA-Z]/.test(address);
          }

          return true;
        })
        .max(64, 'validation.max64')
        .required('validation.required'),
    otherwise: (schema) => schema.nullable(),
  }),
  city: Yup.string().when('institutionVariant', {
    is: (v: string) => v === 'custom',
    then: (schema) =>
      schema
        .test('required', 'validation.required_field', (city, { parent }) => {
          const address = parent.address;
          const country_code = parent.country;
          const post_code = parent.postCode;

          if (address || country_code || post_code) {
            return Boolean(city);
          }

          return true;
        })
        .max(64, 'validation.max64')
        .required('validation.required'),
    otherwise: (schema) => schema.nullable(),
  }),
  postCode: Yup.string().when('institutionVariant', {
    is: (v: string) => v === 'custom',
    then: (schema) =>
      schema
        .test('required', 'validation.required_field', (post_code, { parent }) => {
          const city = parent.city;
          const address = parent.address;
          const country_code = parent.country;

          if (city || address || country_code) {
            return Boolean(post_code);
          }

          return true;
        })
        .test('zip-test', 'validation.zip_format', (zip, testContext) => {
          const country = testContext.parent.country;
          if (!zip || !country || !postcodeValidatorExistsForCountry(country)) {
            return true;
          }

          return postcodeValidator(zip, country);
        })

        .max(16, 'validation.max16')
        .required('validation.required'),
    otherwise: (schema) => schema.nullable(),
  }),
  country: Yup.mixed().when('institutionVariant', {
    is: (v: string) => v === 'custom',
    then: (schema) => schema.required('validation.required'),
    otherwise: (schema) => schema.nullable(),
  }),

  addressInfo: Yup.string().nullable(),
  info: Yup.string().nullable(),

  contactPhone: Yup.string()
    .test('phone-country', 'validation.phone_format', function (v: any) {
      if (v && v.length !== 4) {
        const phone = parsePhoneNumberFromString(v);
        return phone?.isValid() || false;
      }
      return true;
    })
    .required('validation.required'),
  contactEmail: Yup.string().email('validation.email').required('validation.required'),
  medicalInsurances: Yup.array().of(Yup.mixed()).required('validation.required'),
  workspaceTags: Yup.array().of(Yup.string().required()).required('validation.required'),
});
